import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import './App.css';

function Chat() {
  // User role state - load from localStorage if available
  const [userRole, setUserRole] = useState(() => {
    return localStorage.getItem('userRole') || null;
  });
  const [showRoleSelector, setShowRoleSelector] = useState(() => {
    return !localStorage.getItem('userRole');
  });
  const [showCustomNameInput, setShowCustomNameInput] = useState(false);
  const [customName, setCustomName] = useState('');

  // Message and compose state
  const [messages, setMessages] = useState([]); // Array of { role, text, timestamp, clientId }
  const [composeTexts, setComposeTexts] = useState({}); // Object of clientId -> { text, role }
  const [myComposeText, setMyComposeText] = useState('');

  // UI state
  const [connectionStatus, setConnectionStatus] = useState('Connecting...');
  const [fontSize, setFontSize] = useState(14);
  const [isComposeExpanded, setIsComposeExpanded] = useState(false);
  const [showSettings, setShowSettings] = useState(false);

  // Color settings (synced with server)
  const [roleColors, setRoleColors] = useState({
    'Speaker': '#000000',
    'Clerk': '#0000FF',
    'Deputy Clerk': '#008000',
    'Unknown': '#7C3AED'
  });

  // Character limit setting (stored in localStorage)
  const [characterLimit, setCharacterLimit] = useState(() => {
    const saved = localStorage.getItem('characterLimit');
    return saved ? parseInt(saved, 10) : 300;
  });

  // Test mode setting (synced with server, initialized from server on mount)
  const [testMode, setTestMode] = useState(false);
  const testModeInitialized = useRef(false);

  // Persistent session ID (stored in localStorage)
  const persistentSessionId = useRef(null);
  if (!persistentSessionId.current) {
    let sessionId = localStorage.getItem('persistentSessionId');
    if (!sessionId) {
      // Generate a random animal name for the session ID
      const animals = [
        'Aardvark', 'Albatross', 'Alligator', 'Alpaca', 'Ant', 'Anteater', 'Antelope', 'Ape',
        'Armadillo', 'Baboon', 'Badger', 'Barracuda', 'Bat', 'Bear', 'Beaver', 'Bee',
        'Bison', 'Boar', 'Buffalo', 'Butterfly', 'Camel', 'Capybara', 'Caribou', 'Cassowary',
        'Cat', 'Caterpillar', 'Cattle', 'Chamois', 'Cheetah', 'Chicken', 'Chimpanzee', 'Chinchilla',
        'Chough', 'Clam', 'Cobra', 'Cockroach', 'Cod', 'Cormorant', 'Coyote', 'Crab',
        'Crane', 'Crocodile', 'Crow', 'Curlew', 'Deer', 'Dinosaur', 'Dog', 'Dogfish',
        'Dolphin', 'Donkey', 'Dotterel', 'Dove', 'Dragonfly', 'Duck', 'Dugong', 'Dunlin',
        'Eagle', 'Echidna', 'Eel', 'Eland', 'Elephant', 'Elk', 'Emu', 'Falcon',
        'Ferret', 'Finch', 'Fish', 'Flamingo', 'Fly', 'Fox', 'Frog', 'Gaur',
        'Gazelle', 'Gerbil', 'Giraffe', 'Gnat', 'Gnu', 'Goat', 'Goldfinch', 'Goldfish',
        'Goose', 'Gorilla', 'Goshawk', 'Grasshopper', 'Grouse', 'Guanaco', 'Gull', 'Hamster',
        'Hare', 'Hawk', 'Hedgehog', 'Heron', 'Herring', 'Hippopotamus', 'Hornet', 'Horse',
        'Hummingbird', 'Hyena', 'Ibex', 'Ibis', 'Jackal', 'Jaguar', 'Jay', 'Jellyfish',
        'Kangaroo', 'Kingfisher', 'Koala', 'Kookabura', 'Kouprey', 'Kudu', 'Lapwing', 'Lark',
        'Lemur', 'Leopard', 'Lion', 'Llama', 'Lobster', 'Locust', 'Loris', 'Louse',
        'Lyrebird', 'Magpie', 'Mallard', 'Manatee', 'Mandrill', 'Mantis', 'Marten', 'Meerkat',
        'Mink', 'Mole', 'Mongoose', 'Monkey', 'Moose', 'Mosquito', 'Mouse', 'Mule',
        'Narwhal', 'Newt', 'Nightingale', 'Octopus', 'Okapi', 'Opossum', 'Oryx', 'Ostrich',
        'Otter', 'Owl', 'Oyster', 'Panther', 'Parrot', 'Partridge', 'Peafowl', 'Pelican',
        'Penguin', 'Pheasant', 'Pig', 'Pigeon', 'Pony', 'Porcupine', 'Porpoise', 'Quail',
        'Quelea', 'Quetzal', 'Rabbit', 'Raccoon', 'Rail', 'Ram', 'Rat', 'Raven',
        'Reindeer', 'Rhinoceros', 'Rook', 'Salamander', 'Salmon', 'Sandpiper', 'Sardine', 'Scorpion',
        'Seahorse', 'Seal', 'Shark', 'Sheep', 'Shrew', 'Skunk', 'Snail', 'Snake',
        'Sparrow', 'Spider', 'Spoonbill', 'Squid', 'Squirrel', 'Starling', 'Stingray', 'Stork',
        'Swallow', 'Swan', 'Tapir', 'Tarsier', 'Tiger', 'Toad', 'Trout', 'Turkey',
        'Turtle', 'Viper', 'Vulture', 'Wallaby', 'Walrus', 'Wasp', 'Weasel', 'Whale',
        'Wildcat', 'Wolf', 'Wolverine', 'Wombat', 'Woodcock', 'Woodpecker', 'Worm', 'Wren',
        'Yak', 'Zebra'
      ];
      const animalName = animals[Math.floor(Math.random() * animals.length)];
      const randomSuffix = Math.random().toString(36).substring(2, 7).toUpperCase();
      sessionId = `${animalName}-${randomSuffix}`;
      localStorage.setItem('persistentSessionId', sessionId);
    }
    persistentSessionId.current = sessionId;
  }

  // Refs
  const socketRef = useRef(null);
  const messageWindowRef = useRef(null);
  const composeWindowRef = useRef(null);
  const isConnecting = useRef(false);
  const isMounted = useRef(true);

  // Fetch initial test mode and role colors from server on mount
  useEffect(() => {
    if (testModeInitialized.current) return;
    testModeInitialized.current = true;

    const fetchServerSettings = async () => {
      try {
        const isLocalhost = window.location.hostname === 'localhost' ||
                            window.location.hostname === '127.0.0.1' ||
                            window.location.hostname.startsWith('192.168.');
        const baseUrl = isLocalhost ? 'http://localhost:5000' : '';

        const response = await fetch(`${baseUrl}/status`);
        const data = await response.json();

        if (data.testMode !== undefined) {
          setTestMode(data.testMode);
        }
        if (data.roleColors) {
          setRoleColors(data.roleColors);
        }
      } catch (error) {
        console.error('Failed to fetch server settings:', error);
      }
    };

    fetchServerSettings();
  }, []);

  // Save character limit to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('characterLimit', characterLimit.toString());
  }, [characterLimit]);

  // Save user role to localStorage when it changes
  useEffect(() => {
    if (userRole) {
      localStorage.setItem('userRole', userRole);
    }
  }, [userRole]);

  // Auto-scroll message window to bottom when new messages arrive
  useEffect(() => {
    if (messageWindowRef.current) {
      messageWindowRef.current.scrollTop = messageWindowRef.current.scrollHeight;
    }
  }, [messages]);

  // Function to handle role selection
  const handleRoleSelection = (role) => {
    if (role === 'Other') {
      setShowCustomNameInput(true);
    } else {
      setUserRole(role);
      setShowRoleSelector(false);
    }
  };

  // Function to handle custom name submission
  const handleCustomNameSubmit = () => {
    const trimmedName = customName.trim();
    if (trimmedName) {
      setUserRole(trimmedName);
      setShowRoleSelector(false);
      setShowCustomNameInput(false);
      setCustomName('');
    }
  };

  // Function to change role (disconnect and show role selector)
  const handleChangeRole = () => {
    // Disconnect WebSocket
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }
    // Clear messages and compose texts
    setMessages([]);
    setComposeTexts({});
    setMyComposeText('');
    // Show role selector
    setShowRoleSelector(true);
  };

  // WebSocket connection
  useEffect(() => {
    if (!userRole || isConnecting.current) {
      return;
    }

    isConnecting.current = true;
    isMounted.current = true;

    const isLocalhost = window.location.hostname === 'localhost' ||
                        window.location.hostname === '127.0.0.1' ||
                        window.location.hostname.startsWith('192.168.');

    const socketUrl = isLocalhost
      ? 'ws://localhost:5000'
      : `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`;

    console.log(`Connecting to WebSocket: ${socketUrl}`);

    let reconnectTimeout;
    let reconnectAttempts = 0;
    const maxReconnectAttempts = 5;

    const connect = () => {
      if (socketRef.current && socketRef.current.readyState !== WebSocket.CLOSED) {
        socketRef.current.close();
      }

      socketRef.current = new WebSocket(socketUrl);

      socketRef.current.onopen = () => {
        console.log('WebSocket connection opened');
        setConnectionStatus('Connected ✓');
        reconnectAttempts = 0;

        // Send client info with role and persistent session ID
        const clientInfo = {
          type: '__client_info__',
          hostname: window.location.hostname || 'unknown',
          userAgent: navigator.userAgent,
          role: userRole,
          sessionId: persistentSessionId.current
        };
        socketRef.current.send(JSON.stringify(clientInfo));

        if (reconnectTimeout) {
          clearTimeout(reconnectTimeout);
        }
      };

      socketRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);

          // Handle initial state
          if (data.type === 'initial_state') {
            setMessages(data.messages || []);
            const composeObj = {};
            (data.composeTexts || []).forEach(item => {
              composeObj[item.clientId] = { text: item.text, role: item.role, ip: item.ip, sessionId: item.sessionId };
            });
            setComposeTexts(composeObj);
            setTestMode(data.testMode || false);
            if (data.roleColors) {
              setRoleColors(data.roleColors);
            }
            return;
          }

          // Handle new message
          if (data.type === 'new_message') {
            setMessages(prev => [...prev, data.message]);
            // Auto-scroll to bottom
            setTimeout(() => {
              if (messageWindowRef.current) {
                messageWindowRef.current.scrollTop = messageWindowRef.current.scrollHeight;
              }
            }, 0);
            return;
          }

          // Handle compose update from other users
          if (data.type === 'compose_update') {
            setComposeTexts(prev => ({
              ...prev,
              [data.clientId]: { text: data.text, role: data.role, ip: data.ip, sessionId: data.sessionId }
            }));
            return;
          }

          // Handle compose clear
          if (data.type === 'compose_clear') {
            setComposeTexts(prev => {
              const newCompose = { ...prev };
              delete newCompose[data.clientId];
              return newCompose;
            });
            return;
          }

          // Handle clear all
          if (data.type === 'clear_all') {
            setMessages([]);
            setComposeTexts({});
            setMyComposeText('');
            return;
          }

          // Handle test mode update
          if (data.type === 'test_mode_update') {
            setTestMode(data.enabled);
            return;
          }

          // Handle role colors update
          if (data.type === 'role_colors_update') {
            setRoleColors(data.roleColors);
            return;
          }

        } catch (e) {
          console.error('Error parsing message:', e);
        }
      };

      socketRef.current.onerror = (error) => {
        console.error('WebSocket connection error:', error);
        setConnectionStatus('Connection Error ✗');
      };

      socketRef.current.onclose = (event) => {
        console.log('WebSocket closed - Code:', event.code);

        if (!isMounted.current) {
          return;
        }

        setConnectionStatus('Disconnected ✗');

        if (reconnectAttempts < maxReconnectAttempts && event.code !== 1000 && event.code !== 1005) {
          reconnectAttempts++;
          reconnectTimeout = setTimeout(() => {
            if (isMounted.current) {
              console.log(`Reconnecting (${reconnectAttempts}/${maxReconnectAttempts})...`);
              setConnectionStatus('Reconnecting...');
              connect();
            }
          }, 3000);
        } else if (event.code !== 1000 && event.code !== 1005) {
          setConnectionStatus('Connection Failed ✗');
        }
      };
    };

    connect();

    return () => {
      isMounted.current = false;
      isConnecting.current = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
    };
  }, [userRole]);

  // Handle compose text change
  const handleComposeChange = (e) => {
    const newText = e.target.value;

    // If text exceeds limit, auto-send the current text and keep the overflow
    if (newText.length > characterLimit) {
      // Send the portion up to the limit
      const textToSend = newText.substring(0, characterLimit);
      if (textToSend.trim() && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'submit_message',
          text: textToSend
        }));
      }

      // Keep the overflow text in compose
      const overflow = newText.substring(characterLimit);
      setMyComposeText(overflow);

      // Send compose update with overflow text
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'compose_update',
          text: overflow
        }));
      }
      return;
    }

    setMyComposeText(newText);

    // Send compose update to server
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify({
        type: 'compose_update',
        text: newText
      }));
    }
  };

  // Handle Enter key (submit message)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (myComposeText.trim()) {
        // Send message to server
        if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
          socketRef.current.send(JSON.stringify({
            type: 'submit_message',
            text: myComposeText
          }));
        }
        setMyComposeText('');
      }
      return;
    }

    // F6 - Decrease font size
    if (e.key === 'F6') {
      e.preventDefault();
      setFontSize(prev => Math.max(10, prev - 2));
      return;
    }

    // F7 - Increase font size
    if (e.key === 'F7') {
      e.preventDefault();
      setFontSize(prev => Math.min(32, prev + 2));
      return;
    }

    // F9 - Clear compose window
    if (e.key === 'F9') {
      e.preventDefault();
      setMyComposeText('');
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'clear_compose'
        }));
      }
      return;
    }

    // F10 - Clear all
    if (e.key === 'F10') {
      e.preventDefault();
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.send(JSON.stringify({
          type: 'clear_all'
        }));
      }
      return;
    }
  };

  // Role selection screen
  if (showRoleSelector) {
    return (
      <div className="App">
        <div className="role-selector">
          {!showCustomNameInput ? (
            <>
              <h2>Select Your Role</h2>
              <div className="role-buttons">
                <button onClick={() => handleRoleSelection('Speaker')}>
                  Speaker
                </button>
                <button onClick={() => handleRoleSelection('Clerk')}>
                  Clerk
                </button>
                <button onClick={() => handleRoleSelection('Deputy Clerk')}>
                  Deputy Clerk
                </button>
                <button onClick={() => handleRoleSelection('Other')}>
                  Other
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>Enter Your Name</h2>
              <div className="custom-name-input">
                <input
                  type="text"
                  placeholder="Enter your name..."
                  value={customName}
                  onChange={(e) => setCustomName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCustomNameSubmit();
                    }
                  }}
                  autoFocus
                />
                <div className="custom-name-buttons">
                  <button onClick={handleCustomNameSubmit} disabled={!customName.trim()}>
                    Continue
                  </button>
                  <button onClick={() => {
                    setShowCustomNameInput(false);
                    setCustomName('');
                  }}>
                    Back
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Settings panel
  const SettingsPanel = () => (
    <div className="settings-overlay" onClick={() => setShowSettings(false)}>
      <div className="settings-panel" onClick={(e) => e.stopPropagation()}>
        <h3>Settings</h3>

        {/* Character Limit Setting */}
        <div className="settings-section">
          <h4>Character Limit</h4>
          <div className="character-limit-setting">
            <label>Maximum characters per message:</label>
            <input
              type="number"
              min="100"
              max="5000"
              value={characterLimit}
              onChange={(e) => setCharacterLimit(parseInt(e.target.value, 10) || 600)}
            />
          </div>
          <div className="settings-note">
            Messages will auto-send when this limit is reached
          </div>
        </div>

        {/* Test Mode Setting */}
        <div className="settings-section">
          <h4>Test Mode</h4>
          <div className="test-mode-setting">
            <label>
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => {
                  const newTestMode = e.target.checked;
                  setTestMode(newTestMode);
                  // Send test mode toggle to server
                  if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                    socketRef.current.send(JSON.stringify({
                      type: 'toggle_test_mode',
                      enabled: newTestMode
                    }));
                  }
                }}
              />
              Show client IDs in messages
            </label>
          </div>
          <div className="settings-note">
            When enabled, displays IP addresses alongside role names for troubleshooting (applies to all connected clients)
          </div>
        </div>

        {/* Color Settings */}
        <div className="settings-section">
          <h4>Text Colours</h4>
          <div className="color-settings">
            {Object.keys(roleColors).map(role => (
              <div key={role} className="color-setting-row">
                <label>{role} Text Colour</label>
                <input
                  type="color"
                  value={roleColors[role]}
                  onChange={(e) => {
                    const newColors = {
                      ...roleColors,
                      [role]: e.target.value
                    };
                    setRoleColors(newColors);
                    // Send to server
                    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                      socketRef.current.send(JSON.stringify({
                        type: 'update_role_colors',
                        roleColors: newColors
                      }));
                    }
                  }}
                />
                <div
                  className="color-preview"
                  style={{ backgroundColor: roleColors[role] }}
                />
              </div>
            ))}
          </div>
          <div className="settings-note">
            Color changes apply to all connected clients
          </div>
        </div>

        <div className="settings-buttons">
          <button onClick={() => setShowSettings(false)}>Save and Close</button>
          <button onClick={() => setShowSettings(false)}>Cancel</button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="App">
      <div className="chamber-chat-container">
        {/* Menu Bar */}
        <div className="menu-bar">
          <span>Chamber Chat</span>
          <div className="menu-items">
            <button onClick={handleChangeRole}>Change Role</button>
            <button onClick={() => setShowSettings(true)}>Settings</button>
            <Link to="/status" className="status-link-menu">Status</Link>
            <div
              className="connection-status-menu"
              style={{
                color: connectionStatus.includes('Connected') ? '#48bb78' : '#e53e3e'
              }}
            >
              {connectionStatus}
            </div>
          </div>
        </div>

        {/* Message Window (top) */}
        <div
          className="message-window"
          ref={messageWindowRef}
          style={{ fontSize: `${fontSize}px` }}
        >
          {messages.map((msg, idx) => (
            <div key={idx} style={{ color: roleColors[msg.role] || roleColors['Unknown'] }}>
              <strong>[{msg.role}{testMode && msg.ip ? ` - ${msg.ip}` : ''}{testMode && msg.sessionId ? ` (${msg.sessionId})` : ''}]</strong> {msg.text}
            </div>
          ))}
        </div>

        {/* Compose Window (bottom) */}
        <div className={`compose-section ${isComposeExpanded ? 'expanded' : ''}`}>
          <div className="compose-header">
            <span>Compose Window - Your Role: {userRole}</span>
            <button
              className="expand-button"
              onClick={() => setIsComposeExpanded(!isComposeExpanded)}
            >
              {isComposeExpanded ? '▼ Collapse' : '▲ Expand Compose Window'}
            </button>
          </div>

          {/* Other users' compose texts */}
          {Object.keys(composeTexts).length > 0 && (
            <div className="others-composing" style={{ fontSize: `${fontSize}px` }}>
              {Object.entries(composeTexts).map(([clientId, data]) => (
                <div key={clientId} style={{ color: roleColors[data.role] || roleColors['Unknown'] }}>
                  <em>[{data.role}{testMode && data.ip ? ` - ${data.ip}` : ''}{testMode && data.sessionId ? ` (${data.sessionId})` : ''} typing...]</em> {data.text}
                </div>
              ))}
            </div>
          )}

          {/* My compose area */}
          <textarea
            ref={composeWindowRef}
            className="compose-textarea"
            value={myComposeText}
            onChange={handleComposeChange}
            onKeyDown={handleKeyDown}
            placeholder="Type your message here... (Press Enter to send, F9 to clear, F10 to clear all)"
            spellCheck="false"
            style={{ fontSize: `${fontSize}px` }}
          />

          <div className="compose-footer">
            <span>Characters: {myComposeText.length}/{characterLimit}</span>
            <span>
              <span className="footer-action" onClick={() => setFontSize(prev => Math.max(10, prev - 2))}>F6: Smaller</span> | {' '}
              <span className="footer-action" onClick={() => setFontSize(prev => Math.min(32, prev + 2))}>F7: Larger</span> | {' '}
              <span className="footer-action" onClick={() => {
                setMyComposeText('');
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                  socketRef.current.send(JSON.stringify({ type: 'clear_compose' }));
                }
              }}>F9: Clear</span> | {' '}
              <span className="footer-action" onClick={() => {
                if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                  socketRef.current.send(JSON.stringify({ type: 'clear_all' }));
                }
              }}>F10: Clear All</span> | {' '}
              <span className="footer-action" onClick={() => {
                if (myComposeText.trim() && socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
                  socketRef.current.send(JSON.stringify({ type: 'submit_message', text: myComposeText }));
                  setMyComposeText('');
                }
              }}>Enter: Send</span>
            </span>
          </div>
        </div>

        {/* Settings Panel */}
        {showSettings && <SettingsPanel />}
      </div>
    </div>
  );
}

export default Chat;
